import React, { useState, Component } from 'react';
import { Box, Image, Text, Flex, Button, Badge, useToast, IconButton, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, useDisclosure, Stack } from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, Truck, ShieldCheck, ArrowLeft, ShoppingCart, Zap, Package, Share2, Facebook, Instagram, Link as LinkIcon, MessageCircle } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { ProductCard } from '../components/ProductCard';
export const ProductDetail: React.FC = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const toast = useToast();
  const {
    products,
    addToCart,
    promoCodes,
    adminSettings
  } = useAppContext();
  const {
    isOpen,
    onOpen,
    onClose
  } = useDisclosure();
  const product = products.find(p => p.id === id);
  const [activeImage, setActiveImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [addedToCart, setAddedToCart] = useState(false);
  if (!product) {
    return <Flex direction="column" align="center" justify="center" minH="60vh" p={4} data-id="element-545">
        <Package size={48} color="var(--chakra-colors-gray-300)" data-id="element-546" />
        <Text mt={4} color="gray.500" data-id="element-547">
          Product not found
        </Text>
      </Flex>;
  }
  const discountPercent = product.price > 0 ? Math.round((product.price - product.discountPrice) / product.price * 100) : 0;
  const similarProducts = products.filter(p => p.category === product.category && p.id !== product.id).slice(0, 4);
  // Find active promo for this product
  const activePromo = promoCodes.find(p => p.isActive && new Date(p.expiryDate) > new Date() && (p.productIds.length === 0 || p.productIds.includes(product.id)));
  const validateSelection = () => {
    if (product.sizes && product.sizes.length > 0 && !selectedSize) {
      toast({
        title: 'Please select a size',
        status: 'warning',
        duration: 2000
      });
      return false;
    }
    if (product.colors && product.colors.length > 0 && !selectedColor) {
      toast({
        title: 'Please select a color',
        status: 'warning',
        duration: 2000
      });
      return false;
    }
    return true;
  };
  const handleAddToCart = () => {
    if (!validateSelection()) return;
    addToCart(product, 1, selectedSize, selectedColor);
    setAddedToCart(true);
    toast({
      title: '✓ Added to cart',
      status: 'success',
      duration: 2000,
      position: 'top'
    });
    setTimeout(() => setAddedToCart(false), 600);
  };
  const handleBuyNow = () => {
    if (!validateSelection()) return;
    addToCart(product, 1, selectedSize, selectedColor);
    navigate('/checkout');
  };
  const shareText = `🛍️ Check out ${product.title} on Sky Brand!\n💰 Price: ₹${product.discountPrice} (was ₹${product.price})\n🏷️ ${discountPercent}% OFF!\n${activePromo ? `🎟️ Use Promo Code: ${activePromo.code} to get extra ${activePromo.discountType === 'percentage' ? `${activePromo.discountValue}%` : `₹${activePromo.discountValue}`} off!\n` : ''}🔗 ${window.location.href}`;
  const handleShareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');
    onClose();
  };
  const handleShareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}&quote=${encodeURIComponent(shareText)}`, '_blank');
    onClose();
  };
  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareText);
    toast({
      title: 'Link copied to clipboard!',
      status: 'success',
      duration: 2000
    });
    onClose();
  };
  return <Box bg="gray.50" pb="80px" className={adminSettings.animationsEnabled ? 'animate-fade-in' : ''} data-id="element-548">
      {/* Header */}
      <Flex align="center" justify="space-between" p={3} bg="white" position="sticky" top="56px" zIndex={5} className="glass-effect" data-id="element-549">
        <Flex align="center" data-id="element-550">
          <IconButton aria-label="Back" icon={<ArrowLeft size={20} data-id="element-552" />} variant="ghost" size="sm" mr={2} borderRadius="full" onClick={() => navigate(-1)} data-id="element-551" />
          <Text fontWeight="600" fontSize="sm" noOfLines={1} color="gray.700" maxW="250px" data-id="element-553">
            {product.title}
          </Text>
        </Flex>
        <IconButton aria-label="Share" icon={<Share2 size={18} data-id="element-555" />} variant="ghost" size="sm" borderRadius="full" onClick={onOpen} data-id="element-554" />
      </Flex>

      {/* Share Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered data-id="element-556">
        <ModalOverlay data-id="element-557" />
        <ModalContent mx={4} borderRadius="16px" data-id="element-558">
          <ModalHeader fontSize="md" fontWeight="700" data-id="element-559">
            Share Product
          </ModalHeader>
          <ModalCloseButton data-id="element-560" />
          <ModalBody pb={6} data-id="element-561">
            <Stack spacing={3} data-id="element-562">
              <Button w="full" colorScheme="green" leftIcon={<MessageCircle size={18} data-id="element-564" />} onClick={handleShareWhatsApp} justifyContent="flex-start" data-id="element-563">
                Share on WhatsApp
              </Button>
              <Button w="full" colorScheme="facebook" leftIcon={<Facebook size={18} data-id="element-566" />} onClick={handleShareFacebook} justifyContent="flex-start" data-id="element-565">
                Share on Facebook
              </Button>
              <Button w="full" bgGradient="linear(to-r, #833AB4, #FD1D1D, #F77737)" color="white" _hover={{
              opacity: 0.9
            }} leftIcon={<Instagram size={18} data-id="element-568" />} onClick={handleCopyLink} justifyContent="flex-start" data-id="element-567">
                Share on Instagram (Copy Link)
              </Button>
              <Button w="full" variant="outline" leftIcon={<LinkIcon size={18} data-id="element-570" />} onClick={handleCopyLink} justifyContent="flex-start" data-id="element-569">
                Copy Link
              </Button>
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Image Gallery */}
      <Box bg="white" p={4} className={adminSettings.animationsEnabled ? 'animate-slide-up' : ''} data-id="element-571">
        <Box borderRadius="16px" overflow="hidden" mb={3} data-id="element-572">
          <Image src={product.images[activeImage]} alt={product.title} w="full" h="320px" objectFit="contain" transition="all 0.4s ease" data-id="element-573" />
        </Box>
        <Flex gap={2} overflowX="auto" sx={{
        '&::-webkit-scrollbar': {
          display: 'none'
        }
      }} data-id="element-574">
          {product.images.map((img, idx) => <Box key={idx} borderWidth="2px" borderColor={activeImage === idx ? adminSettings.accentColor : 'gray.200'} borderRadius="10px" overflow="hidden" cursor="pointer" onClick={() => setActiveImage(idx)} transition="all 0.2s ease" data-id="element-575">
              <Image src={img} w="56px" h="56px" objectFit="cover" data-id="element-576" />
            </Box>)}
        </Flex>
      </Box>

      {/* Product Info */}
      <Box bg="white" p={4} mt={2} className={adminSettings.animationsEnabled ? 'animate-slide-up-delay-1' : ''} data-id="element-577">
        <Text fontSize="lg" fontWeight="600" mb={2} color="gray.800" lineHeight="1.4" data-id="element-578">
          {product.title}
        </Text>
        <Flex align="center" mb={3} gap={2} data-id="element-579">
          <Flex align="center" bg="green.500" color="white" px={2} py={0.5} borderRadius="8px" data-id="element-580">
            <Text fontSize="xs" fontWeight="700" mr={1} data-id="element-581">
              {product.rating || 'New'}
            </Text>
            <Star size={10} fill="currentColor" data-id="element-582" />
          </Flex>
          <Text fontSize="xs" color="gray.400" data-id="element-583">
            {product.reviews || 0} ratings
          </Text>
        </Flex>
        <Flex align="baseline" gap={3} mb={1} data-id="element-584">
          <Text fontSize="2xl" fontWeight="800" color="gray.900" data-id="element-585">
            ₹{product.discountPrice}
          </Text>
          {product.price > product.discountPrice && <>
              <Text fontSize="sm" color="gray.400" textDecoration="line-through" data-id="element-586">
                ₹{product.price}
              </Text>
              <Text fontSize="sm" color="green.500" fontWeight="700" data-id="element-587">
                {discountPercent}% OFF
              </Text>
            </>}
        </Flex>
        <Text fontSize="10px" color="gray.400" mb={3} data-id="element-588">
          Inclusive of all taxes
        </Text>
        <Badge bg="green.50" color="green.600" px={3} py={1} borderRadius="8px" fontWeight="600" fontSize="xs" data-id="element-589">
          ✓ Cash On Delivery Available
        </Badge>
      </Box>

      {/* Size & Color Selection */}
      <Box bg="white" p={4} mt={2} className={adminSettings.animationsEnabled ? 'animate-slide-up-delay-2' : ''} data-id="element-590">
        {product.sizes && product.sizes.length > 0 && <Box mb={4} data-id="element-591">
            <Text fontWeight="700" mb={2} fontSize="sm" color="gray.700" data-id="element-592">
              Select Size
            </Text>
            <Flex gap={2} wrap="wrap" data-id="element-593">
              {product.sizes.map(size => <Button key={size} variant={selectedSize === size ? 'solid' : 'outline'} bg={selectedSize === size ? adminSettings.accentColor : 'transparent'} color={selectedSize === size ? 'white' : 'gray.600'} borderColor={selectedSize === size ? adminSettings.accentColor : 'gray.200'} onClick={() => setSelectedSize(size)} size="sm" minW="44px" borderRadius="10px" fontWeight="600" data-id="element-594">
                  {size}
                </Button>)}
            </Flex>
          </Box>}
        {product.colors && product.colors.length > 0 && <Box data-id="element-595">
            <Text fontWeight="700" mb={2} fontSize="sm" color="gray.700" data-id="element-596">
              Select Color
            </Text>
            <Flex gap={3} wrap="wrap" data-id="element-597">
              {product.colors.map(color => <Box key={color} w="34px" h="34px" borderRadius="full" bg={color.toLowerCase()} cursor="pointer" borderWidth={selectedColor === color ? '3px' : '2px'} borderColor={selectedColor === color ? adminSettings.accentColor : 'gray.200'} onClick={() => setSelectedColor(color)} title={color} shadow={selectedColor === color ? 'md' : 'sm'} data-id="element-598" />)}
            </Flex>
          </Box>}
      </Box>

      {/* Delivery Info */}
      <Box bg="white" p={4} mt={2} data-id="element-599">
        <Flex align="center" mb={3} data-id="element-600">
          <Truck size={18} color={adminSettings.accentColor} data-id="element-601" />
          <Text ml={3} fontSize="sm" fontWeight="500" color="gray.600" data-id="element-602">
            Free Delivery in Ghazipur
          </Text>
        </Flex>
        <Flex align="center" data-id="element-603">
          <ShieldCheck size={18} color="var(--chakra-colors-green-500)" data-id="element-604" />
          <Text ml={3} fontSize="sm" fontWeight="500" color="gray.600" data-id="element-605">
            100% Genuine Product
          </Text>
        </Flex>
      </Box>

      {/* Description */}
      <Box bg="white" p={4} mt={2} data-id="element-606">
        <Text fontWeight="700" mb={2} fontSize="sm" color="gray.700" data-id="element-607">
          Description
        </Text>
        <Text fontSize="sm" color="gray.500" lineHeight="1.7" whiteSpace="pre-wrap" data-id="element-608">
          {product.description}
        </Text>
      </Box>

      {/* Similar Products */}
      {similarProducts.length > 0 && <Box bg="white" p={4} mt={2} data-id="element-609">
          <Text fontWeight="700" mb={4} fontSize="sm" color="gray.700" data-id="element-610">
            You May Also Like
          </Text>
          <Flex overflowX="auto" gap={3} pb={2} sx={{
        '&::-webkit-scrollbar': {
          display: 'none'
        }
      }} data-id="element-611">
            {similarProducts.map((p, idx) => <Box minW="160px" key={p.id} data-id="element-612">
                <ProductCard product={p} index={idx} data-id="element-613" />
              </Box>)}
          </Flex>
        </Box>}

      {/* Fixed Bottom Actions */}
      <Flex position="fixed" bottom="72px" w="full" maxW="480px" bg="white" p={3} shadow="0 -4px 20px rgba(0,0,0,0.08)" gap={3} zIndex={10} data-id="element-614">
        <Button flex={1} variant="outline" borderColor={adminSettings.accentColor} color={adminSettings.accentColor} leftIcon={<ShoppingCart size={16} data-id="element-616" />} onClick={handleAddToCart} borderRadius="12px" fontWeight="700" className={addedToCart && adminSettings.animationsEnabled ? 'animate-cart-bounce' : ''} data-id="element-615">
          Add to Cart
        </Button>
        <Button flex={1} bg={adminSettings.accentColor} color="white" leftIcon={<Zap size={16} data-id="element-618" />} onClick={handleBuyNow} borderRadius="12px" fontWeight="700" _hover={{
        opacity: 0.9
      }} data-id="element-617">
          Buy Now
        </Button>
      </Flex>
    </Box>;
};